import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import { useUser } from "../contexts/UserContext";
import { useMsal } from "@azure/msal-react";
import { Save, Trash2, Edit2, Plus, X } from "lucide-react";
import { TanStackDynamicTable } from "../components/TanStackDynamicTable";
import { ColumnDef, SortingState, ColumnFiltersState } from "@tanstack/react-table";
import {
  InventoryItemDto,
  getInventoryItems,
  createInventoryItem,
  updateInventoryItem,
  deleteInventoryItem,
} from "../services/inventoryItemApiService";

// Extend the DTO with an `id` field so TanStackDynamicTable's getRowId works
type InventoryItemRow = InventoryItemDto & { id: string | number };

const InventoryItemsAdminPage: React.FC = () => {
  const { isAdmin } = useUser();
  const { instance, accounts } = useMsal();

  const [allItems, setAllItems] = useState<InventoryItemRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  // Table state
  const [tableState, setTableState] = useState<{
    sorting: SortingState;
    pagination: { pageIndex: number; pageSize: number };
    columnFilters: ColumnFiltersState;
  }>({
    sorting: [],
    pagination: { pageIndex: 0, pageSize: 99999 },
    columnFilters: [],
  });

  // Form state
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formInventoryId, setFormInventoryId] = useState("");
  const [formDesc, setFormDesc] = useState("");
  const [formUom, setFormUom] = useState("");

  const refresh = async () => {
    if (accounts.length === 0) return;
    setLoading(true);
    try {
      const raw = await getInventoryItems(instance, accounts[0]);
      setAllItems(raw.map((item, idx) => ({ ...item, id: item.inventoryId ?? idx })));
    } catch {
      setMsg("Failed to load inventory items.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isAdmin || accounts.length === 0) return;
    refresh();
    // eslint-disable-next-line
  }, [isAdmin, instance, accounts]);

  // Client-side sort + paginate
  const { pageData, totalCount } = useMemo(() => {
    let result = [...allItems];

    // Sort
    if (tableState.sorting.length > 0) {
      const { id, desc } = tableState.sorting[0];
      result.sort((a, b) => {
        const aVal = (a as any)[id] ?? "";
        const bVal = (b as any)[id] ?? "";
        if (aVal < bVal) return desc ? 1 : -1;
        if (aVal > bVal) return desc ? -1 : 1;
        return 0;
      });
    }

    const total = result.length;

    // Paginate
    const { pageIndex, pageSize } = tableState.pagination;
    const start = pageIndex * pageSize;
    result = result.slice(start, start + pageSize);

    return { pageData: result, totalCount: total };
  }, [allItems, tableState.sorting, tableState.pagination]);

  const resetForm = () => {
    setEditingId(null);
    setFormInventoryId("");
    setFormDesc("");
    setFormUom("");
  };

  const startEdit = useCallback((item: InventoryItemRow) => {
    setEditingId(item.inventoryId);
    setFormInventoryId(String(item.inventoryId ?? ""));
    setFormDesc(item.inventoryDesc ?? "");
    setFormUom(item.uom ?? "");
  }, []);

  const handleDeleteItem = useCallback(async (inventoryId: string) => {
    if (!window.confirm("Delete this inventory item?")) return;
    try {
      await deleteInventoryItem(instance, accounts[0], inventoryId);
      setEditingId((prev) => (prev === inventoryId ? null : prev));
      if (editingId === inventoryId) resetForm();
      await refresh();
    } catch {
      setMsg("Failed to delete inventory item.");
    }
    // eslint-disable-next-line
  }, [instance, accounts, editingId]);

  const handleSave = async () => {
    const id = formInventoryId.trim() || null;
    setMsg(null);
    try {
      if (editingId == null) {
        await createInventoryItem(instance, accounts[0], {
          inventoryId: id,
          inventoryDesc: formDesc.trim() || null,
          uom: formUom.trim() || null,
        });
      } else {
        await updateInventoryItem(instance, accounts[0], editingId, {
          inventoryId: id,
          inventoryDesc: formDesc.trim() || null,
          uom: formUom.trim() || null,
        });
      }
      resetForm();
      await refresh();
    } catch {
      setMsg("Failed to save inventory item.");
    }
  };

  const columns: ColumnDef<InventoryItemRow>[] = useMemo(
    () => [
      {
        id: "inventoryId",
        header: "Inventory ID",
        accessorKey: "inventoryId",
        meta: { type: "text" },
      },
      {
        id: "inventoryDesc",
        header: "Description",
        accessorKey: "inventoryDesc",
        meta: { type: "text" },
      },
      {
        id: "uom",
        header: "UOM",
        accessorKey: "uom",
        meta: { type: "text" },
        size: 100,
      },
      {
        id: "actions",
        header: "Actions",
        size: 100,
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex items-center gap-1">
            <button
              onClick={() => startEdit(row.original)}
              className="p-1.5 text-slate-500 hover:text-primary transition-colors"
              title="Edit"
            >
              <Edit2 size={15} />
            </button>
            {row.original.inventoryId != null && (
              <button
                onClick={() => handleDeleteItem(row.original.inventoryId!)}
                className="p-1.5 text-slate-500 hover:text-rose-500 transition-colors"
                title="Delete"
              >
                <Trash2 size={15} />
              </button>
            )}
          </div>
        ),
      },
    ],
    [startEdit, handleDeleteItem]
  );

  if (!isAdmin) {
    return (
      <div className="p-8 text-center">
        <h1 className="text-xl font-bold text-slate-900 dark:text-white">Access Denied</h1>
        <p className="text-slate-500 mt-2">You do not have permission to view this page.</p>
        <Link to="/" className="text-primary hover:underline mt-4 block">Go to Dashboard</Link>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Header + Form */}
      <div className="shrink-0 bg-white dark:bg-surface-dark border-b border-slate-200 dark:border-slate-800 p-6">
        <h1 className="text-2xl font-extrabold text-slate-900 dark:text-white mb-4">Inventory Items</h1>

        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Inventory ID</label>
            <input
              type="text"
              value={formInventoryId}
              onChange={(e) => setFormInventoryId(e.target.value)}
              placeholder="ID"
              disabled={editingId != null}
              className="w-32 px-3 py-2 text-sm rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-background-dark text-slate-900 dark:text-white disabled:opacity-50"
            />
          </div>
          <div className="flex flex-col gap-1 flex-1 min-w-[200px]">
            <label className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Description</label>
            <input
              type="text"
              value={formDesc}
              onChange={(e) => setFormDesc(e.target.value)}
              placeholder="Item description"
              className="px-3 py-2 text-sm rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-background-dark text-slate-900 dark:text-white"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">UOM</label>
            <input
              type="text"
              value={formUom}
              onChange={(e) => setFormUom(e.target.value)}
              placeholder="e.g. TN"
              className="w-28 px-3 py-2 text-sm rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-background-dark text-slate-900 dark:text-white"
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleSave}
              className="flex items-center gap-1.5 px-4 py-2 text-sm bg-primary hover:bg-primary/90 text-white font-semibold rounded-md transition-colors"
            >
              {editingId == null ? <Plus size={14} /> : <Save size={14} />}
              {editingId == null ? "Add Item" : "Update Item"}
            </button>
            {editingId != null && (
              <button
                onClick={resetForm}
                className="flex items-center gap-1.5 px-4 py-2 text-sm bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 dark:hover:bg-slate-600 text-slate-800 dark:text-slate-100 font-semibold rounded-md transition-colors"
              >
                <X size={14} />
                Cancel
              </button>
            )}
          </div>
        </div>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Inventory Item must match up to an Acumuatica Item.
          </p>

        {msg && (
          <div className="mt-3 text-sm text-rose-600 dark:text-rose-400">{msg}</div>
        )}
      </div>

      {/* Table */}
      <div className="flex-1 overflow-hidden p-6">
        {loading ? (
          <div className="flex items-center justify-center h-full text-sm text-slate-500">Loading...</div>
        ) : (
          <TanStackDynamicTable
            data={pageData}
            columns={columns}
            rowCount={totalCount}
            tableName="inventory_items_admin"
            defaultPageSize={totalCount || 1}
            showPagination={false}
            onStateChange={setTableState}
          />
        )}
      </div>
    </div>
  );
};

export default InventoryItemsAdminPage;
